import classNames from 'classnames';
import { useContext, useEffect, useRef, useState } from 'react';
import CardFlip from '../../../components/CardFlip';
import { useTranslation } from 'react-i18next';
import stripHtml from '../../../utils/wordlist/stripHtml';
import WordlistContext, { EnrichedWord } from './_contexts/WordlistContext';
import WordlistViewModeContext, { WordlistViewMode } from './_contexts/WordlistViewModeContext';

function nl2br(str: string) {
  if (str) {
    return str.replace(/[\r\n]+/g, '<br />');
  }
  return str;
}

type WordCardProps = {
  word: EnrichedWord;
  isFavorite: boolean;
  onFavorite: () => void;
  flipped: boolean;
};

const ListenAction = ({ onClick, isPlaying }: { onClick: () => void; isPlaying: boolean }) => (
  <button className="word-card__action" onClick={onClick}>
    <svg role="img" className="icon">
      <use href={isPlaying ? '#icon-speech-mute' : '#icon-audible'} />
    </svg>
  </button>
);

const FavoriteAction = ({ onClick, isFavorite }: { onClick: () => void; isFavorite: boolean }) => (
  <button className="word-card__action" onClick={onClick}>
    <svg role="img" className="icon">
      <use href={isFavorite ? '#icon-favorite-full' : '#icon-favorite'} />
    </svg>
  </button>
);

const ExpandAction = ({ onClick, isExpanded }: { onClick: () => void; isExpanded: boolean }) => (
  <button className="word-card__action" onClick={onClick}>
    <svg role="img" className="icon">
      <use href={isExpanded ? '#icon-card-hide' : '#icon-card-show'} />
    </svg>
  </button>
);

const useVoice = (word: EnrichedWord) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);

  const hasAudio = Boolean(word.s3MaleVoice || word.s3FemaleVoice);

  const playAudio = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
      audioRef.current?.play();
    }
  };

  useEffect(() => {
    setIsPlaying(false);
  }, [word]);

  useEffect(() => {
    const audio = audioRef.current;
    const callback = () => {
      setIsPlaying(false);
    };

    if (audio) {
      audio.addEventListener('ended', callback);

      return () => {
        audio.removeEventListener('ended', callback);
      };
    }
  }, [audioRef]);

  return { hasAudio, isPlaying, playAudio, ref: audioRef };
};

export default function WordCard({ word, isFavorite, flipped, onFavorite }: WordCardProps) {
  const { t } = useTranslation();
  const { viewMode } = useContext(WordlistViewModeContext);
  const { cardsExpanded } = useContext(WordlistContext);
  const [isExpanded, setIsExpanded] = useState(false);

  const { hasAudio, isPlaying, playAudio, ref: audioRef } = useVoice(word);

  useEffect(() => {
    setIsExpanded(cardsExpanded);
  }, [cardsExpanded, word]);

  return (
    <CardFlip
      flipped={flipped}
      className={classNames('word-card', {
        'word-card--collapsed': !isExpanded,
      })}
      id="word-card"
    >
      {hasAudio && (
        <audio
          data-slot="audio"
          src={word.s3MaleVoice || word.s3FemaleVoice}
          ref={audioRef}
          data-testid="word-card-audio"
        />
      )}
      <div className="word-card__front" data-slot="front" data-testid="word-card__front">
        <div className="word-card__untranslated word-card--dark">
          <header className="word-card__header">
            <span className="word-card__word" dangerouslySetInnerHTML={{ __html: stripHtml(word.untranslated) }} />
            <div className="word-card__actions">
              {hasAudio && <ListenAction onClick={playAudio} isPlaying={isPlaying} />}
              <FavoriteAction onClick={onFavorite} isFavorite={isFavorite} />
              {viewMode === WordlistViewMode.FOCUS && (
                <ExpandAction onClick={() => setIsExpanded(!isExpanded)} isExpanded={isExpanded} />
              )}
            </div>
          </header>
          <div className="word-card__body">
            <div className="word-card__meta">
              {word.filters?.wordType && <p data-testid="word-wordtype">{word.filters.wordType}</p>}
              {word.extra && <div data-testid="word-extra" dangerouslySetInnerHTML={{ __html: word.extra }} />}
              {word.sentence && <div data-testid="word-sentence" dangerouslySetInnerHTML={{ __html: word.sentence }} />}
            </div>
            {word.s3Image && (
              <div style={{ backgroundImage: `url(${word.s3Image})` }} className="word-card__image" role="img" />
            )}
          </div>
        </div>
        <div className="word-card__translated word-card--light">
          <span className="word-card__word" dangerouslySetInnerHTML={{ __html: stripHtml(word.translated) }} />
          {word.synonyms && (
            <div className="word-card__synonyms" data-testid="word-synonyms">
              {t('MODULES.MATERIAL.COMPONENTS.WORD_LISTS.SYNONYMS')}{' '}
              <div dangerouslySetInnerHTML={{ __html: nl2br(word.synonyms) }} />
            </div>
          )}
        </div>
      </div>
      <div className="word-card__back" data-slot="back" data-testid="word-card__back">
        <div className="word-card__translated word-card--light">
          <header className="word-card__header">
            <span className="word-card__word" dangerouslySetInnerHTML={{ __html: stripHtml(word.translated) }} />
            <div className="word-card__actions">
              {hasAudio && <ListenAction onClick={playAudio} isPlaying={isPlaying} />}
              <FavoriteAction onClick={onFavorite} isFavorite={isFavorite} />
              {viewMode === WordlistViewMode.FOCUS && (
                <ExpandAction onClick={() => setIsExpanded(!isExpanded)} isExpanded={isExpanded} />
              )}
            </div>
          </header>
        </div>
        <div className="word-card__untranslated word-card--dark">
          <p className="word-card__word" dangerouslySetInnerHTML={{ __html: stripHtml(word.untranslated) }} />
          <div className="word-card__body">
            <div className="word-card__meta">
              {word.filters?.wordType && <p data-testid="word-wordtype">{word.filters.wordType}</p>}
              {word.extra && <div data-testid="word-extra" dangerouslySetInnerHTML={{ __html: word.extra }} />}
              {word.sentence && <div data-testid="word-sentence" dangerouslySetInnerHTML={{ __html: word.sentence }} />}
              {word.synonyms && (
                <div data-testid="word-synonyms">
                  <p>
                    <b> {t('MODULES.MATERIAL.COMPONENTS.WORD_LISTS.OTHER_TRANSLATIONS')}</b>
                  </p>
                  <div dangerouslySetInnerHTML={{ __html: nl2br(word.synonyms) }} />
                </div>
              )}
            </div>
            {word.s3Image && (
              <div style={{ backgroundImage: `url(${word.s3Image})` }} className="word-card__image" role="img" />
            )}
          </div>
        </div>
      </div>
    </CardFlip>
  );
}
